import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  DollarSign,
  Users,
  Edit,
  Loader2,
  Building2,
  Briefcase,
  Hash,
  FileText,
} from 'lucide-react';
import { reservationService } from '../services';
import { Reservation } from '../types';

export const OperationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [operation, setOperation] = useState<Reservation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadOperation(parseInt(id));
    }
  }, [id]);

  const loadOperation = async (operationId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await reservationService.getReservationById(String(operationId));
      setOperation(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load operation');
    } finally {
      setLoading(false);
    }
  };

  const getStatusVariant = (
    status: string
  ): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'PENDING':
        return 'outline';
      case 'CONFIRMED':
        return 'default';
      case 'COMPLETED':
        return 'secondary';
      case 'CANCELLED':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !operation) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/operations')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Operations
        </Button>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error || 'Operation not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/operations')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Operation #{operation.id}</h1>
            <p className="text-muted-foreground">Operation Details</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={getStatusVariant(operation.status)}>{operation.status}</Badge>
          <Button variant="outline">
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Operation Information */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Building2 className="h-4 w-4" />
                Branch
              </div>
              <p className="text-base">{operation.branch?.name || '-'}</p>
              <p className="text-sm text-muted-foreground">{operation.branch?.address || ''}</p>
            </div>

            <Separator />

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                Business Owner
              </div>
              <p className="text-base">{operation.businessOwner?.fullName || '-'}</p>
              <p className="text-sm text-muted-foreground">
                {operation.businessOwner?.email || ''}
              </p>
            </div>

            {operation.businessPartner && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                    <Users className="h-4 w-4" />
                    Business Partner
                  </div>
                  <p className="text-base">{operation.businessPartner.fullName}</p>
                  <p className="text-sm text-muted-foreground">{operation.businessPartner.email}</p>
                </div>
              </>
            )}

            {operation.jobApplication && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                    <Briefcase className="h-4 w-4" />
                    Related Job Application
                  </div>
                  <p className="text-base">Job #{operation.jobApplication.jobListingId}</p>
                  <p className="text-sm text-muted-foreground">
                    Application ID: {operation.jobApplication.id}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Operation Details */}
        <Card>
          <CardHeader>
            <CardTitle>Operation Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <Hash className="h-4 w-4" />
                  Unit
                </div>
                <p className="text-base">
                  {operation.unit?.name || '-'} ({operation.unit?.abbreviation || '-'})
                </p>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-1">Quantity</div>
                <p className="text-base">{operation.quantity || 0}</p>
              </div>
            </div>

            <Separator />

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Calendar className="h-4 w-4" />
                Start Date & Time
              </div>
              <p className="text-base">
                {operation.dateTime ? new Date(operation.dateTime).toLocaleString() : '-'}
              </p>
            </div>

            {operation.endDateTime && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                    <Calendar className="h-4 w-4" />
                    End Date & Time
                  </div>
                  <p className="text-base">{new Date(operation.endDateTime).toLocaleString()}</p>
                </div>
              </>
            )}

            {operation.commission !== undefined && operation.commission !== null && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                    <DollarSign className="h-4 w-4" />
                    Commission
                  </div>
                  <p className="text-2xl font-bold">${operation.commission.toFixed(2)}</p>
                </div>
              </>
            )}

            {operation.notes && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                    <FileText className="h-4 w-4" />
                    Notes
                  </div>
                  <p className="text-base whitespace-pre-wrap">{operation.notes}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Operation Metadata</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Calendar className="h-4 w-4" />
                Created At
              </div>
              <p className="text-base">{new Date(operation.createdAt).toLocaleString()}</p>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Calendar className="h-4 w-4" />
                Last Updated
              </div>
              <p className="text-base">{new Date(operation.updatedAt).toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OperationDetailPage;
